import { MouseEvent } from "react";
import { MessageSquare, Target } from "lucide-react";
import { PendingInboxItem } from "@forgepoint/shared";
import { InboxItemRow } from "./InboxItemRow";
import { InboxStageTag } from "./InboxStageTag";
import { InboxThreadGroupData } from "./groupInboxItems";

export interface InboxThreadGroupProps {
  group: InboxThreadGroupData;
  agentMap: Map<string, { name: string }>;
  standardAgentName: string;
  onItemClick: (item: PendingInboxItem) => void;
  onItemCancel?: (e: MouseEvent, item: PendingInboxItem) => void;
  completed?: boolean;
  isAdmin?: boolean;
  currentUserId?: string;
}

/**
 * Renders one inbox thread/run group. A lone task renders as a plain row (no
 * card chrome) so the common single-task case stays light; a thread with
 * multiple tasks gets a card with a shared header carrying the thread name,
 * opportunity, and stage, with its task rows beneath.
 * @param group - The grouped inbox items and their shared header context
 * @param agentMap - Map of agent workflow IDs to agent details
 * @param standardAgentName - Display name for the standard agent
 * @param onItemClick - Handler for clicking a task row
 * @param onItemCancel - Handler for cancelling a task row (omitted for completed items)
 * @param completed - Whether this group holds completed items (dimmed, no cancel)
 * @param isAdmin - Whether the current user is an admin (gates the cancel button)
 * @param currentUserId - The current user's id (gates the cancel button to assignees)
 */
export function InboxThreadGroup({
  group,
  agentMap,
  standardAgentName,
  onItemClick,
  onItemCancel,
  completed,
  isAdmin,
  currentUserId,
}: InboxThreadGroupProps) {
  if (group.items.length === 1) {
    const item = group.items[0];
    return (
      <InboxItemRow
        item={item}
        agentMap={agentMap}
        standardAgentName={standardAgentName}
        onClick={() => onItemClick(item)}
        onCancel={onItemCancel ? (e) => onItemCancel(e, item) : undefined}
        completed={completed}
        isAdmin={isAdmin}
        currentUserId={currentUserId}
      />
    );
  }

  return (
    <div className="rounded-lg border border-border bg-muted/20 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 bg-muted/40 border-b border-border/60">
        <MessageSquare className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <span className="text-sm font-semibold truncate">{group.title}</span>
        <span className="rounded-full bg-foreground/10 px-1.5 py-px text-[10px] font-medium tabular-nums text-foreground/70 shrink-0">
          {group.items.length}
        </span>
        {group.opportunityName && (
          <span className="inline-flex items-center gap-1 text-xs text-foreground/55 min-w-0">
            <Target className="h-3 w-3 shrink-0" />
            <span className="truncate">{group.opportunityName}</span>
          </span>
        )}
        <div className="ml-auto shrink-0">
          {group.stageName && <InboxStageTag name={group.stageName} />}
        </div>
      </div>
      <div className="flex flex-col gap-px p-1.5 pl-3">
        {/* Vertical rail visually nests the rows under the group header. */}
        <div className="flex flex-col gap-0.5 border-l-2 border-border/50 pl-2">
          {group.items.map((item) => (
            <InboxItemRow
              key={item.taskId}
              item={item}
              agentMap={agentMap}
              standardAgentName={standardAgentName}
              onClick={() => onItemClick(item)}
              onCancel={onItemCancel ? (e) => onItemCancel(e, item) : undefined}
              completed={completed}
              isAdmin={isAdmin}
              currentUserId={currentUserId}
              grouped
            />
          ))}
        </div>
      </div>
    </div>
  );
}
